// Global Instructions Rule Applied!

import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../core/utils/errorToast.tsx'
import type { RootState } from '../../state_manager/store.ts'
import { getAccessToken } from '../../core/auth/authService'
import { store } from '../../state_manager/store.ts'
import { exportJsonToExcel } from '../../core/services/utilitiesService.ts'
import { buildReportingGridExcelPayload } from '../../state_manager/utils/reportingGridExcelExport.ts'
import { fetchAllFundsForExport } from '../shared/table_grid_export/fetchAllFilteredForExport.ts'
import { getReportingGridColumnsForReportId } from '../../state_manager/utils/reportingGridColumns.ts'
import { useReportingGridColumnsWithPreferences } from '../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import { getAddColumnsCatalogForReport } from '../../state_manager/utils/reportingColumnCatalog.ts'
import type { ViewConfigColumn } from '../../core/components/ViewConfig/types.ts'
import { useFundsData } from './hooks/useFundsData.ts'
import { useFundsReportId } from '../../state_manager/hooks/useFundsReportId.ts'
import { FundsTable } from './components/FundsTable.tsx'
import { Button } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useTabManagerContext } from '../../core/app-layout/lib/TabManagerContext.tsx'
import ViewToolbar from '../../core/components/ViewToolbar/index.tsx'
import { RIBBON_CTA_CLASS } from '../../core/components/ViewToolbar/index.tsx'
import { ViewConfig } from '../../core/components/ViewConfig/index.ts'
import { DEFAULT_FUNDS_TABLE_FILTERS } from '../global_configs/tableViewPreferences.ts'
import { TABLE_FUNDS } from '../../state_manager/globals.ts'
import { FundRecordFields } from '../../state_manager/data_models/fund_record.ts'
import { ConsolidatedFundsSection } from '../fund_groups/components/ConsolidatedFundsSection.tsx'

export default function FundsPage() {
  const reportingConfigs = useSelector((state: RootState) => state.globalConfigs?.reporting_configs ?? [])
  const reportingColumnDefaults = useSelector(
    (state: RootState) => state.globalConfigs?.reporting_column_defaults ?? []
  )
  const reportingColumns = useSelector((state: RootState) => state.globalConfigs?.reporting_columns ?? [])

  const fundsReportId = useFundsReportId()
  const { openFormInTab } = useTabManagerContext()
  // Creating a fund is admin-gated (cfg.app_admins; POST /funds returns 403
  // otherwise), so the button only renders for admins — nobody sees a control
  // they cannot use.
  const isAppAdmin = useSelector((state: RootState) => state.globalConfigs?.is_app_admin === true)

  const {
    fundsData,
    fundsDataLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    filterOptions,
    filterOptionsLoading,
    handleFetchFunds,
    handleSetFilters,
    handleSetSorting,
    handleResetFilters,
    handleLoadMore,
    defaultTableSorting,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  } = useFundsData()

  // Grid columns honor user column-key preferences when set; otherwise the
  // default order from per-report cfg.reporting_columns.
  const gridColumns = useReportingGridColumnsWithPreferences(fundsReportId, preferenceRow)

  const viewConfigColumns = gridColumns

  // Catalog of columns that can be added/hidden in the Edit Columns drawer:
  // primary-module ("Fund") columns and related-module columns from cfg.
  const addColumnsCatalog = useMemo(
    () =>
      getAddColumnsCatalogForReport(
        fundsReportId,
        {
          reporting_columns: reportingColumns,
          reporting_column_defaults: reportingColumnDefaults
        },
        { primaryModuleKey: 'funds' }
      ),
    [fundsReportId, reportingColumns, reportingColumnDefaults]
  )

  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )

  const defaultTableColumnKeys = useMemo(() => {
    if (!fundsReportId) return null
    const rows = getReportingGridColumnsForReportId(fundsReportId, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    })
    return rows.map((r) => r.valueKey)
  }, [fundsReportId, reportingConfigs, reportingColumnDefaults, reportingColumns])

  const columnByValueKey = useMemo(() => {
    const m = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) m.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) m.set(c.valueKey, c)
    for (const c of viewConfigColumns) {
      if (!m.has(c.valueKey)) m.set(c.valueKey, c)
    }
    return m
  }, [addColumnsCatalog, viewConfigColumns])

  const resolveViewConfigColumn = useCallback(
    (valueKey: string) => columnByValueKey.get(valueKey),
    [columnByValueKey]
  )

  const fundsColumnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    }),
    [
      persistColumnFieldOrder,
      resetColumnLayoutToDefault,
      resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    ]
  )

  const [exportLoading, setExportLoading] = useState(false)

  const handleExportTable = useCallback(async () => {
    setExportLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const { data, error } = await fetchAllFundsForExport(
        activeSorting,
        activeFilters,
        token,
        fundsReportId ?? null
      )
      if (error) {
        showErrorToast(error)
        return
      }
      // Drop the grand-total row from the export — Excel users typically
      // total in the spreadsheet themselves.
      const rows = data.filter((r: any) => r && r.funds__id) as Record<string, unknown>[]
      if (rows.length === 0) {
        toast.info('No rows to export')
        return
      }
      const payload = buildReportingGridExcelPayload(gridColumns, rows)
      const filename = `Funds - ${dayjs().format('YYYY-MM-DD')}.xlsx`
      const result = await exportJsonToExcel({ ...payload, filename }, token)
      if (result.success) {
        toast.success(`${filename} exported successfully`)
      } else {
        showErrorToast(result.error || 'Export failed')
      }
    } finally {
      setExportLoading(false)
    }
  }, [activeSorting, activeFilters, fundsReportId, gridColumns])

  return (
    <div className='h-full overflow-hidden'>
      <div className='w-full h-full flex flex-col bg-white px-2 sm:px-4 pb-2 sm:pb-4 overflow-hidden'>
        <ViewToolbar
          moduleKey={TABLE_FUNDS}
          recordTitleField={FundRecordFields.funds__name}
          searchPlaceholder='Search Funds...'
          searchClickOpensFullForm
          addButtonEnabled={false}
          activeFilters={activeFilters}
          onRefreshData={(filters) =>
            handleFetchFunds(activeSorting, Array.isArray(filters) ? filters : activeFilters)
          }
          onFilterChange={(filters) => handleSetFilters(Array.isArray(filters) ? filters : [])}
          defaultFilters={DEFAULT_FUNDS_TABLE_FILTERS}
          onResetFilters={handleResetFilters}
          exportEnabled
          onExport={handleExportTable}
          exportLoading={exportLoading}
          beforeAddSlot={
            <>
              <ViewConfig
                moduleTitle='Funds'
                columns={viewConfigColumns}
                filterOptions={filterOptions}
                filterOptionsLoading={filterOptionsLoading}
                addColumnsCatalog={addColumnsCatalog}
                addColumnsTabLabels={{ primary: 'Fund', related: 'Related' }}
                columnLayout={fundsColumnLayout}
                filteringConfig={{
                  currentFilters: activeFilters,
                  defaultFilters: DEFAULT_FUNDS_TABLE_FILTERS,
                  onApplyFilters: async (filters) => {
                    handleSetFilters(filters)
                  },
                  onResetToDefaults: async () => {
                    handleSetFilters(DEFAULT_FUNDS_TABLE_FILTERS)
                  }
                }}
                sortingConfig={{
                  currentSorting: activeSorting,
                  defaultSorting: defaultTableSorting,
                  onApplySorting: async (sorting) => {
                    handleSetSorting(sorting)
                  },
                  onResetToDefaults: async () => {
                    handleSetSorting(defaultTableSorting)
                  }
                }}
              />
              {/* Funds open a full form (no drawer), so the stock AddButton's
                  drawer route doesn't apply - open the form tab. */}
              {isAppAdmin ? (
                <Button
                  type='primary'
                  icon={<PlusOutlined />}
                  onClick={() => openFormInTab(TABLE_FUNDS, null)}
                  className={RIBBON_CTA_CLASS}
                >
                  Fund
                </Button>
              ) : null}
            </>
          }
        />

        <div className='flex-1 min-h-0 h-full flex flex-col'>
          <FundsTable
            data={fundsData}
            loading={fundsDataLoading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            currentPage={currentPage}
            gridColumns={gridColumns}
            activeFilters={activeFilters}
            activeSorting={activeSorting}
            filterOptions={filterOptions}
            filterOptionsLoading={filterOptionsLoading}
            onLoadMore={handleLoadMore}
            onFiltersChange={(filters) => {
              const filterArray = Object.entries(filters).map(([key, value]) => ({ key, value }))
              handleSetFilters(filterArray)
            }}
            onSortChange={(sortConfig: any) => {
              if (sortConfig) {
                handleSetSorting([{ key: sortConfig.column, direction: sortConfig.direction }])
              } else {
                handleSetSorting([])
              }
            }}
          />
        </div>

        {/* Consolidated funds (fund groups): a second section below the
            funds list — one row per group with the deduped consolidated
            figures. Renders nothing when no groups exist. */}
        <div className='mt-2'>
          <ConsolidatedFundsSection embedded />
        </div>
      </div>
    </div>
  )
}
